import { LanguagesEnum } from '@/api/enums';
import { handleAxiosError } from '@/api/helpers';
import { client } from '@/client';
import { Course } from '@/client/courses';
import { CourseAttempt } from '@/client/courses/types';
import { CourseStatusAttemptEnum } from '@/client/courses/types/courses.type';
import {
  FeedbackAnswer,
  ModifiedQuiz,
  QuestionType,
} from '@/client/quizzes/types';
import { RedirectPaths, RedirectPathsEnum } from '@/common/constants';
import { useToast } from '@/hooks/useToast';
import FeedbackEndPlaceholder from '@/images/placeholders/feedback-end.png';
import hardcodedTranslations from '@/locales/hardcodedTranslations';
import { AppSpinner } from '@/ui/app-spinner';
import { AppButton } from '@/ui/buttons';
import { ProgressHeader } from '@/ui/progress-header/ProgressHeader';
import { StarRating } from '@/ui/star-rating';
import { FlexContainer } from '@/ui/styled-ui';
import { modifyCourseQuiz } from '@/utils/helpers/courses.helper';
import { isMobile } from '@/utils/helpers/ui.helper';
import { AxiosError } from 'axios';
import { InputTextarea } from 'primereact/inputtextarea';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { css } from 'styled-components';

const StyledQuizContainer = styled(FlexContainer)`
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
  height: 100%;
  max-width: 720px;
`;

const StyledQuizQuestionContainer = styled(FlexContainer)<{
  isMobile: boolean;
}>`
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  gap: var(--default-padding);
  padding-bottom: 93px;
  border-bottom: 1px solid var(--gray-dark);

  ${({ isMobile }) =>
    isMobile &&
    css`
      gap: var(--small-padding);
      margin-bottom: 0px;
      border-bottom: none;
    `}
`;

const StyledQuizNavigationContainer = styled(FlexContainer)`
  flex-direction: row;
  justify-content: flex-end;
  gap: var(--small-padding);
  padding-top: var(--semi-big-padding);

  .p-button.p-button-outlined {
    background: transparent !important;
  }
`;

const StyledFeedbackDiv = styled(FlexContainer)`
  flex-direction: column;
  padding-bottom: 46px;
  border-bottom: 1px solid var(--gray-dark);

  h1 {
    width: 100%;
    font-size: 20px;
    font-weight: 400;
  }

  textarea {
    width: 100%;
    height: 130px;
    max-width: 720px;
    padding: var(--medium-padding);
    align-items: flex-start;
    gap: 8px;
    flex-shrink: 0;
    background: var(--white-main);
    border-radius: 15px;
    border: 1px solid var(--gray-moderate);
    resize: none;
  }
`;

const StyledLoadingContainer = styled(FlexContainer)`
  width: 100%;
  height: 100%;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--default-padding);
`;

type QuizFeedbackProps = {
  quizFeedbackId?: string;
  course: Course;
  attemptLanguage: LanguagesEnum;
  rtl?: boolean;
  courseAttempt?: CourseAttempt;
  onComplete: (score: number) => void;
  handleBackToCourse?: () => void;
  isPreview?: boolean;
  onExit?: () => void;
  status?: CourseStatusAttemptEnum;
};

export const QuizFeedback: React.FC<QuizFeedbackProps> = ({
  quizFeedbackId,
  course,
  courseAttempt,
  isPreview = false,
  onComplete,
  onExit,
  attemptLanguage,
  rtl,
}) => {
  const navigate = useNavigate();
  const toast = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [quizFeedback, setQuizFeedback] = useState<ModifiedQuiz | null>(null);
  const [answers, setAnswers] = useState<Record<number, FeedbackAnswer>>({});
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState<number>(0);
  const [showCompleteScreen, setShowCompleteScreen] = useState<boolean>(false);

  useEffect(() => {
    const fetchQuizFeedback = async () => {
      if (!quizFeedbackId) return;

      try {
        setIsLoading(true);
        const response = await client.courses.getCourseQuiz(
          course.id,
          quizFeedbackId,
        );

        setQuizFeedback(modifyCourseQuiz(response));
      } catch (error) {
        handleAxiosError(error as AxiosError, toast);
      } finally {
        setIsLoading(false);
      }
    };

    fetchQuizFeedback();
  }, [quizFeedbackId, course.id, attemptLanguage]);

  const currentQuestion = useMemo(
    () => quizFeedback?.questions[currentQuestionIndex],
    [quizFeedback, currentQuestionIndex],
  );

  const currentAnswer = useMemo(
    () =>
      answers[currentQuestionIndex] || {
        scaleResponse: 0,
        textResponse: '',
      },
    [answers, currentQuestionIndex],
  );

  const updateAnswer = (
    questionIndex: number,
    updates: Partial<FeedbackAnswer>,
  ) => {
    setAnswers((prev) => ({
      ...prev,
      [questionIndex]: {
        ...prev[questionIndex],
        ...updates,
      },
    }));
  };

  const submitFeedbackAnswer = async (
    scaleResponse?: number,
    textResponse?: string,
  ) => {
    if (!currentQuestion?.id || !courseAttempt) return;

    try {
      await client.learnerCourses.answerFeedbackQuestion(
        course.id,
        currentQuestion.id,
        scaleResponse,
        textResponse,
        courseAttempt.id,
      );
    } catch (error) {
      handleAxiosError(error as AxiosError, toast);
      return false;
    }

    return true;
  };

  const handleRatingChange = (value: number) => {
    updateAnswer(currentQuestionIndex, { scaleResponse: value });
    submitFeedbackAnswer(value);
  };

  const handleNext = async () => {
    const shouldSubmit =
      currentQuestion?.type === QuestionType.TEXT &&
      currentAnswer?.textResponse &&
      currentAnswer.textResponse.length > 0;

    if (!isPreview && courseAttempt && currentQuestion?.id && shouldSubmit) {
      const success = await submitFeedbackAnswer(
        currentAnswer.scaleResponse,
        currentAnswer.textResponse,
      );

      if (!success) return;
    }

    if (currentQuestionIndex === (quizFeedback?.questions?.length || 0) - 1) {
      onComplete(currentAnswer.scaleResponse);
      setShowCompleteScreen(true);
    } else {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }
  };

  if (isLoading) {
    return (
      <StyledLoadingContainer>
        <AppSpinner />
      </StyledLoadingContainer>
    );
  }

  return (
    <>
      <StyledQuizContainer dir={rtl ? 'rtl' : 'ltr'}>
        {!showCompleteScreen && (
          <ProgressHeader
            className="quiz-progress-header"
            currentIndex={currentQuestionIndex}
            totalCount={quizFeedback?.questions?.length || 0}
            attemptLanguage={attemptLanguage}
            type="quiz"
          />
        )}

        <div className="w-full">
          {currentQuestion?.type === QuestionType.RATING_SCALE &&
            !showCompleteScreen && (
              <StyledQuizQuestionContainer isMobile={isMobile()} width="100%">
                <h2>{currentQuestion?.translations[attemptLanguage]?.text}</h2>
                <StarRating
                  value={currentAnswer?.scaleResponse || 0}
                  onChange={handleRatingChange}
                  maxStars={5}
                />
              </StyledQuizQuestionContainer>
            )}

          {currentQuestion?.type === QuestionType.TEXT &&
            !showCompleteScreen && (
              <StyledFeedbackDiv>
                <label htmlFor="feedback-textarea" className="w-full">
                  <h2>
                    {currentQuestion?.translations[attemptLanguage]?.text}
                  </h2>
                </label>
                <InputTextarea
                  maxLength={500}
                  id="feedback-textarea"
                  value={currentAnswer?.textResponse || ''}
                  autoFocus
                  onChange={(e) =>
                    updateAnswer(currentQuestionIndex, {
                      textResponse: e.target.value,
                    })
                  }
                />
              </StyledFeedbackDiv>
            )}

          {showCompleteScreen && (
            <FlexContainer
              direction="column"
              align="center"
              justify="center"
              gap={24}
            >
              <img
                src={FeedbackEndPlaceholder}
                alt={
                  hardcodedTranslations.messages.thankYouForFeedback[
                    attemptLanguage
                  ]
                }
                width="100%"
                height="100%"
                style={{ maxWidth: '300px' }}
              />
              <h2>
                {
                  hardcodedTranslations.messages.thankYouForFeedback[
                    attemptLanguage
                  ]
                }
              </h2>
            </FlexContainer>
          )}
        </div>

        <StyledQuizNavigationContainer>
          {!showCompleteScreen ? (
            <>
              {currentQuestionIndex !== 0 && (
                <AppButton
                  label={
                    hardcodedTranslations.buttons.previous[attemptLanguage]
                  }
                  type="outlined"
                  onClick={() => {
                    setCurrentQuestionIndex(currentQuestionIndex - 1);
                  }}
                />
              )}
              <AppButton
                label={
                  currentQuestionIndex !==
                  (quizFeedback?.questions?.length || 0) - 1
                    ? hardcodedTranslations.buttons.next[attemptLanguage]
                    : hardcodedTranslations.buttons.submit[attemptLanguage]
                }
                severity="success"
                icon={`pi ${rtl ? 'pi-arrow-left' : 'pi-arrow-right'}`}
                iconPos="right"
                rtl={rtl}
                onClick={handleNext}
                isDisabled={
                  currentQuestion?.type === QuestionType.RATING_SCALE &&
                  answers[currentQuestionIndex]?.scaleResponse === 0
                }
              />
            </>
          ) : (
            <AppButton
              label={hardcodedTranslations.buttons.goToContent[attemptLanguage]}
              severity="success"
              icon={`pi ${rtl ? 'pi-arrow-left' : 'pi-arrow-right'}`}
              iconPos="right"
              rtl={rtl}
              className="m-auto"
              autoFocus
              onClick={() => {
                if (onExit) {
                  onExit();
                  return;
                }
                !isPreview
                  ? navigate(
                      RedirectPaths[
                        RedirectPathsEnum.LEARNER_DASHBOARD_ACTIVE
                      ](),
                    )
                  : navigate(RedirectPaths[RedirectPathsEnum.COURSES]());
              }}
            />
          )}
        </StyledQuizNavigationContainer>
      </StyledQuizContainer>
    </>
  );
};
